import React, {
  ChangeEvent,
  FC,
  KeyboardEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Routes from "models/routes";
import { GridToolbarContainer } from "@mui/x-data-grid";
import CommonButton from "components/common/ui/CommonButton";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Box, Grid, InputAdornment, Tooltip } from "@mui/material";
import { css } from "styled-components";
import ColorCircularProgress from "components/common/ui/ColorCircularProgress";
import RecipeStepGridItemBox from "components/food/ui/recipe/RecipeStepGridItemBox";
import SpacedTextInput, {
  SpacedAutocompleteInput,
} from "components/common/ui/SpacedTextInput";
import CircleIconButton from "components/common/ui/CircleIconButton";
import ClearIcon from "@mui/icons-material/Clear";
import SoftSquareIconButton from "components/common/ui/SoftSquareIconButton";
import SearchIcon from "@mui/icons-material/Search";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import foodTagOptionsSelector from "store/selectors/food/foodTagOptionsSelector";
import { actions as foodTagOptionsActions } from "store/slices/food/foodTagOptionsSlice";
import { actions as FoodItemOptionsActions } from "store/slices/food/foodItemOptionsSlice";
import foodItemOptionsSelector from "store/selectors/food/foodItemOptionsSelector";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import dayjs from "dayjs";
import {
  FoodItemSearchingParams,
  FoodItemTag,
  FoodTag,
  FoodTagCategory,
  StepType,
} from "client/jspPlatformExperiment";

const tagCategoriesRank = [
  "Diet Type",
  "Texture",
  "Liquid Thickness",
  "Preference",
];

const getFoodTagCategoryDisplayName = (
  foodTag: FoodTag | FoodItemTag,
): string => {
  let catName = "[Unknown category]";
  if (foodTag.tag_categories.includes(FoodTagCategory.DIET_TYPE)) {
    catName = `Diet Type`;
  } else if (foodTag.tag_categories.includes(FoodTagCategory.FOOD_TEXTURE)) {
    catName = `Texture`;
  } else if (
    foodTag.tag_categories.includes(FoodTagCategory.LIQUID_THICKNESS)
  ) {
    catName = `Liquid Thickness`;
  } else if (foodTag.tag_categories.includes(FoodTagCategory.PREFERENCE)) {
    catName = `Preference`;
  }
  return catName;
};

interface RecipeListTableToolbarProps {
  setSearchingParams: (newSearchingParams: FoodItemSearchingParams) => void;
}

const RecipeListTableToolbar: FC<RecipeListTableToolbarProps> = ({
  setSearchingParams,
}) => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const foodTagOptions = useAppSelector(
    foodTagOptionsSelector.recipeTagOptions,
  );
  const navigate = useNavigate();
  const promptSnackBar = usePromptSnackBar();
  const isDownloading = useAppSelector(
    foodItemOptionsSelector.foodIsDownloading,
  );

  // Parse initial values from URL params
  const initialKeywords = useMemo(
    () => searchParams.get("keywords") || undefined,
    [searchParams],
  );

  const initialHasStepTypes = useMemo(() => {
    const hasStepTypesParam = searchParams.get("hasStepTypes");
    if (hasStepTypesParam && hasStepTypesParam.length > 0) {
      return hasStepTypesParam
        .split(",")
        .filter((type) =>
          [StepType.COOKING, StepType.OVEN, StepType.HUMAN].includes(
            type as StepType,
          ),
        ) as Array<StepType.COOKING | StepType.OVEN | StepType.HUMAN>;
    }
    return [];
  }, [searchParams]);

  const initialHasFoodTags = useMemo(() => {
    const hasFoodTagIdsParam = searchParams.get("hasFoodTagIds");
    if (hasFoodTagIdsParam && hasFoodTagIdsParam.length > 0) {
      const tagIds = hasFoodTagIdsParam
        .split(",")
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));
      // Find the FoodTag objects from foodTagOptions that match these IDs
      return foodTagOptions.filter((tag) => tagIds.includes(tag.id));
    }
    return [];
  }, [searchParams, foodTagOptions]);

  const [displayKeywords, setDisplayKeywords] = useState<string | undefined>(
    initialKeywords,
  );
  const [hasFoodTagsAll, setHasFoodTagsAll] =
    useState<FoodTag[]>(initialHasFoodTags);
  const [hasStepTypesAll, setHasStepTypesAll] =
    useState<(StepType.COOKING | StepType.OVEN | StepType.HUMAN)[]>(
      initialHasStepTypes,
    );

  // Sync local state with URL params when they change externally
  useEffect(() => {
    setDisplayKeywords(initialKeywords);
  }, [initialKeywords]);

  useEffect(() => {
    setHasStepTypesAll(initialHasStepTypes);
  }, [initialHasStepTypes]);

  useEffect(() => {
    setHasFoodTagsAll(initialHasFoodTags);
  }, [initialHasFoodTags]);

  /**
   * Use effect to load the food tag option when first time loading the page
   */
  useEffect(() => {
    if (foodTagOptions.length === 0) {
      dispatch(foodTagOptionsActions.fetchAllFoodTagOptions());
    }
  }, [dispatch, foodTagOptions.length]);

  /**
   * Handle the keywords change
   * @param event
   */
  const handleKeywordsChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value ?? undefined;
    setDisplayKeywords(newValue);
  };

  /**
   * Handle click on create button
   */
  const handleCreateClick = (): void => {
    navigate(Routes.FOOD_RECIPE_CREATE, {
      replace: false,
    });
  };

  /**
   * Handle download all recipes button click
   */
  const handleDownloadClick = useCallback(() => {
    dispatch(
      FoodItemOptionsActions.downloadAllFoodItems({
        downloadType: "recipe",
      }),
    )
      .unwrap()
      .then((response) => {
        // Trigger ZIP file download
        const url = window.URL.createObjectURL(
          new Blob([response], { type: "application/zip" }),
        );
        const alink = document.createElement("a");
        alink.href = url;
        alink.download = `Jasper recipes and ingredients downloaded at ${dayjs().toISOString()}.zip`;
        document.body.appendChild(alink);
        alink.click();
        alink.remove();
      })
      .catch((err) => {
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: `Download error. ${err}`,
          severity: "error",
        });
      });
  }, [dispatch, promptSnackBar]);

  /**
   * Handle click on search button
   */
  const handleSearchClick = useCallback(
    (searchingKeywords: string | undefined) => (): void => {
      setSearchingParams({
        keywords: searchingKeywords,
        has_food_tag_ids_all: hasFoodTagsAll.map((tag) => tag.id),
        has_step_types_all: hasStepTypesAll,
      });
    },
    [hasStepTypesAll, hasFoodTagsAll, setSearchingParams],
  );

  /**
   * Handle click on clear icon in searching bar
   */
  const handleClearClick = useCallback(() => {
    handleSearchClick(undefined)();
    setDisplayKeywords(undefined);
  }, [handleSearchClick]);

  /**
   * Handle keypress while focus on keyword field
   */
  const handleKeypress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        handleSearchClick(displayKeywords)();
      } else if (event.key === "Escape") {
        handleClearClick();
      }
    },
    [displayKeywords, handleClearClick, handleSearchClick],
  );

  /**
   * Handle the include food tags change
   */
  const handleIncludeFoodTagChange = useCallback(
    (_: SyntheticEvent, newValue: FoodTag[]) => {
      setHasFoodTagsAll(newValue);
      setSearchingParams({
        keywords: displayKeywords,
        has_food_tag_ids_all:
          newValue.length > 0 ? newValue.map((tag) => tag.id) : undefined,
        has_step_types_all:
          hasStepTypesAll.length > 0 ? hasStepTypesAll : undefined,
      });
    },
    [displayKeywords, hasStepTypesAll, setSearchingParams],
  );

  /**
   * Handle the include step types change
   */
  const handleIncludeStepTypesChange = useCallback(
    (
      _: SyntheticEvent,
      newValue: (StepType.COOKING | StepType.OVEN | StepType.HUMAN)[],
    ) => {
      setHasStepTypesAll(newValue);
      setSearchingParams({
        keywords: displayKeywords,
        has_food_tag_ids_all:
          hasFoodTagsAll.length > 0
            ? hasFoodTagsAll.map((tag) => tag.id)
            : undefined,
        has_step_types_all: newValue.length > 0 ? newValue : undefined,
      });
    },
    [displayKeywords, hasFoodTagsAll, setSearchingParams],
  );

  return (
    <GridToolbarContainer>
      <Grid container spacing={1} columns={24}>
        <Grid item xs={24} sm={24} md={18} lg={4}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <CommonButton
              text="Create"
              positive
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            />
            <CommonButton
              text="All foods"
              positive={!isDownloading}
              startIcon={
                isDownloading ? (
                  <ColorCircularProgress
                    customColor={css`rgba(0, 0, 0, 0.26)`}
                    size={20}
                  />
                ) : (
                  <FileDownloadIcon />
                )
              }
              onClick={handleDownloadClick}
              disabled={isDownloading}
            />
          </Box>
        </Grid>
        <Grid item xs={24} sm={24} md={7} lg={4}>
          <RecipeStepGridItemBox>
            <SpacedAutocompleteInput
              multiple
              label="Has step (all)"
              size="small"
              options={Object.values(StepType).filter(
                (type) =>
                  type !== StepType.INGREDIENT &&
                  type !== StepType.CHILD_RECIPE,
              )}
              getOptionLabel={(option) => {
                switch (option) {
                  case StepType.COOKING:
                    return "Cooking";
                  case StepType.OVEN:
                    return "Oven";
                  case StepType.HUMAN:
                    return "Human";
                  default:
                    return "";
                }
              }}
              value={hasStepTypesAll}
              onChange={handleIncludeStepTypesChange}
            />
          </RecipeStepGridItemBox>
        </Grid>
        <Grid item xs={24} sm={24} md={17} lg={16}>
          <RecipeStepGridItemBox>
            <SpacedAutocompleteInput
              multiple
              label="Including all food tags"
              size="small"
              options={[...foodTagOptions].sort(
                (a, b) =>
                  tagCategoriesRank.findIndex(
                    (rank) => rank === getFoodTagCategoryDisplayName(a),
                  ) -
                  tagCategoriesRank.findIndex(
                    (rank) => rank === getFoodTagCategoryDisplayName(b),
                  ),
              )}
              groupBy={(option) => getFoodTagCategoryDisplayName(option)}
              getOptionLabel={(option) =>
                option.tag_name ?? `Food tag ID: ${option.id}`
              }
              value={hasFoodTagsAll}
              onChange={handleIncludeFoodTagChange}
            />
          </RecipeStepGridItemBox>
          <RecipeStepGridItemBox>
            <SpacedTextInput
              size="small"
              label="Keywords"
              InputProps={{
                endAdornment: displayKeywords ? (
                  <InputAdornment position="end">
                    <CircleIconButton
                      natural
                      sizepx={24}
                      onClick={handleClearClick}
                    >
                      <ClearIcon />
                    </CircleIconButton>
                  </InputAdornment>
                ) : null,
              }}
              onChange={handleKeywordsChange}
              onKeyDown={handleKeypress}
              value={displayKeywords ?? ""}
            />
            <Box>
              <Tooltip title="Search">
                <Box padding="3px 1rem 3px 0.5rem">
                  <SoftSquareIconButton
                    positive
                    sizepx={34}
                    onClick={handleSearchClick(displayKeywords)}
                  >
                    <SearchIcon />
                  </SoftSquareIconButton>
                </Box>
              </Tooltip>
            </Box>
          </RecipeStepGridItemBox>
        </Grid>
      </Grid>
    </GridToolbarContainer>
  );
};

export default RecipeListTableToolbar;
